import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { CollectionListElementDataType } from '@/components/sections/builder/elements/body/collection-list/type';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import { Input } from '@/components/ui/input';

import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import ModalReset from '@/components/sections/builder/settings/modal-reset';
import { defaultData } from '@/components/sections/builder/elements/body/collection-list';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as CollectionListElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      {/* Start Header Setting */}
      <h1 className='mb-3 text-sm font-bold'>Section Header</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Title Alignment</h4>
        <InputSelect
          defaultValue={data.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>
      <div className='mb-3'>
        <h4 className='mb-2'>Heading Label</h4>
        <TextEditorMinimal
          placeholder='Label'
          onChange={(value) =>
            builder.setElementData(element.id, {
              headingLabel: value,
            })
          }
          value={data.headingLabel}
          className='mb-3 '
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-1',
            },
          }}
          editable
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Heading Size</h4>
        <InputSelect
          defaultValue={data.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              headingSize: v,
            })
          }
        />
      </div>
      <div className='mb-3 space-y-2'>
        <h4 className=''>Text Label</h4>
        <TextEditorMinimal
          placeholder='Label'
          onChange={(value) =>
            builder.setElementData(element.id, {
              textLabel: value,
            })
          }
          value={data.textLabel}
          className='mb-4 w-full '
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-1',
            },
          }}
          editable
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              textStyle: v,
            })
          }
        />
      </div>
      {/* End Header Setting */}
      <Separator className='my-4' />
      {/* Start Collection Card Setting */}
      <h1 className='mb-4 text-sm font-bold'>Layout</h1>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on desktop</h4>
        <InputSlider
          value={data.totalCol || 0}
          max={6}
          onChange={(val) =>
            builder.setElementData(element.id, {
              totalCol: val,
            })
          }
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4>Desktop Layout</h4>
        <InputSelect
          defaultValue={data.desktopLayout || 'row'}
          options={[
            { label: 'Column', value: 'column' },
            { label: 'Row', value: 'row' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              desktopLayout: v,
            })
          }
        />
      </div>
      <div className='mb-4 grid grid-cols-2'>
        <h4>Mobile Layout</h4>
        <div className='flex flex-col gap-y-2'>
          <InputSelect
            defaultValue={data.totalColMobile}
            options={[
              { label: '1 column', value: 'one_column' },
              { label: '2 columns', value: 'two_columns' },
              { label: '3 columns', value: 'three_columns' },
            ]}
            onChange={(v) =>
              builder.setElementData(element.id, {
                totalColMobile: v,
              })
            }
          />
          <label className='text-xs text-gray-500'>Number of columns on mobile</label>
        </div>
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label>Enable swipe on mobile</label>
        <Switch
          className='ml-auto'
          checked={data.enableSwipeMobile}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSwipeMobile: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label>
          Enable &quot;View all&quot; button if list includes more collections than shown
        </label>
        <Switch
          className='ml-auto'
          checked={data.isShowViewAll}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              isShowViewAll: !!checked,
            });
          }}
          aria-readonly
        />
      </div>

      <div className='mb-4 mt-4 font-bold'>&quot;View all&quot; Style</div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label view all</label>
        <Input
          className='w-full p-2 text-xs'
          name='labelViewAll'
          defaultValue={data.labelViewAll}
          value={data.labelViewAll}
          maxLength={30}
          onChange={(e) => {
            builder.setElementData(element.id, {
              labelViewAll: e.target.value,
            });
          }}
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Position view all</label>
        <InputSelect
          defaultValue={data.positionViewAll}
          options={[
            { label: 'Top', value: 'top' },
            { label: 'Bottom', value: 'bottom' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              positionViewAll: v,
            })
          }
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>View all style</label>
        <InputSelect
          defaultValue={data.viewAllStyle}
          options={[
            { label: 'Link', value: 'link' },
            { label: 'Outline button', value: 'outline' },
            { label: 'Solid button', value: 'default' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              viewAllStyle: v,
            })
          }
        />
      </div>

      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />

      {/* End Layout */}
      <Separator className='my-4' />
      {/* Start Style Setting */}

      <h1 className='mb-3 text-sm font-bold'>Collection Items</h1>
      {/*  */}
      <ModalReset
        title=''
        onAction={() => {
          builder.setElementData(element.id, {
            size: defaultData.size,
            styleCollection: defaultData.styleCollection,
            imageRatio: defaultData.imageRatio,
            imageRadius: defaultData.imageRadius,
            itemTitleSize: defaultData.itemTitleSize,
            itemDescriptionSize: defaultData.itemDescriptionSize,
            showViewCollection: defaultData.showViewCollection,
            labelViewCollection: defaultData.labelViewCollection,
            viewCollectionPosition: defaultData.viewCollectionPosition,
          });
        }}
      />
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Collection Size</h4>
        <InputSelect
          defaultValue={data.size}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              size: v,
              totalCol: (() => {
                if (v === 'small') return 4;
                if (v === 'medium') return 3;
                return 2;
              })(),
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Collection Style</h4>
        <InputSelect
          placeholder='Choose style'
          defaultValue={data?.styleCollection ?? ''}
          options={[
            { label: 'Standard', value: 'standard' },
            { label: 'Card', value: 'card' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              styleCollection: v,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Image Ratio</h4>
        <InputSelect
          defaultValue={data.imageRatio}
          options={[
            { label: 'Adapt to image', value: 'adaptToImage' },
            { label: 'Portrait', value: 'portrait' },
            { label: 'Square', value: 'square' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              imageRatio: v,
            })
          }
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Image Radius</h4>
        <InputSlider
          value={data.imageRadius || 0}
          max={40}
          onChange={(val) =>
            builder.setElementData(element.id, {
              imageRadius: val,
            })
          }
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Item Title Size</h4>
        <InputSlider
          value={data.itemTitleSize || 0}
          max={40}
          min={10}
          onChange={(val) =>
            builder.setElementData(element.id, {
              itemTitleSize: val,
            })
          }
        />
      </div>

      <div className='mb-4 flex items-center gap-2'>
        <label>Show Collection Description</label>
        <Switch
          className='ml-auto'
          checked={data.showDescription ?? true}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showDescription: !!checked,
            });
          }}
          aria-readonly
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Item Description Size</h4>
        <InputSlider
          value={data.itemDescriptionSize || 0}
          max={40}
          min={10}
          onChange={(val) =>
            builder.setElementData(element.id, {
              itemDescriptionSize: val,
            })
          }
        />
      </div>

      <div className='mb-4 flex items-center gap-2'>
        <label>Show view collection</label>
        <Switch
          className='ml-auto'
          checked={data.showViewCollection}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showViewCollection: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label view collection</label>
        <Input
          className='w-full p-2 text-xs'
          name='labelViewCollection'
          defaultValue={data.labelViewCollection}
          value={data.labelViewCollection}
          maxLength={30}
          onChange={(e) => {
            builder.setElementData(element.id, {
              labelViewCollection: e.target.value,
            });
          }}
        />
      </div>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>View collection position</label>
        <InputSelect
          defaultValue={data.viewCollectionPosition}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              viewCollectionPosition: v,
            })
          }
        />
      </div>
      {/* End Collection Items */}

      <Separator className='my-4' />
      {/* Start Style Setting */}

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='my-4'>
        <label className=''>Container Color Scheme</label>
        <SchemeSetting
          value={data.colorSchemeCollectionList}
          onChange={(theme) => {
            builder.setElementData(element.id, {
              colorSchemeCollectionList: theme,
            });
          }}
        />
      </div>

      <BackgroundSetting
        value={data.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Overlay</div>
      <ColorPicker
        color={data.overlay}
        inputColor={data.overlay}
        onChangeColorComplete={(color) => onChangeColor(color)}
        onChangeInputColor={(e) => builder.setElementData(element.id, { overlay: e.target.value })}
      />
    </div>
  );
}
